import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import type { DistanceMap } from "../distanceMap";
import type { SomethingWithState } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { deserializeXY } from "../serialization";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { BasicAI } from "../ai/basicAI";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { Chasm } from "../things/chasm";
import { LavaPool } from "../things/lavaPool";
import type { XY } from "../xy";
import { FloatingRobotCorpse } from "./floatingRobotCorpse";

enum FloatingRobotState {
	FLYING = "FLYING",
	OFF = "OFF",
}

class FloatingRobotAI extends BasicAI {
	protected getDistanceMapToPlayer(): DistanceMap | null {
		return this.getBoard().getDistanceMapToPlayerForFlyingThings();
	}
}

export class FloatingRobot extends Mob implements SomethingWithState {
	static key = "FloatingRobot";
	private state: FloatingRobotState = FloatingRobotState.FLYING;
	private ai: BasicAI;

	override getBloodParticleType(): ParticleType {
		return ParticleType.ROBOT_DEBRIS;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(FloatingRobot.key);
	}

	constructor(xy: XY) {
		super(xy);
		this.resistsPermanentFreeze = true;
		this.damageMultipliers.set(DamageType.BUBBLE, 0);
		this.damageMultipliers.set(DamageType.TOXIC_GAS, 0);
		this.damageMultipliers.set(DamageType.MELEE, 0.5);
		this.ai = new FloatingRobotAI(this);
		this.ai.attackSoundSupplier = () => new Sound(SoundId.MOB_PHISICAL_ATTACK, this.xy);
		this.ai.noiseSoundSupplier = () => new Sound(SoundId.ROBOT_SOUND, this.xy);
	}

	canMoveTo(xy: XY): boolean {
		const board = this.board!;
		if (!board.isInBounds(xy)) return false;
		if (board.hasMob(xy)) return false;
		if (board.isPassable(xy)) return true;
		const things = board.getThingsAt(xy);
		return things.some((t) => t instanceof Chasm || t instanceof LavaPool);
	}

	override isFlying(): boolean {
		return true;
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	generateCorpse(): PhysicalThing | null {
		return new FloatingRobotCorpse(this.xy);
	}

	override generateDeathSound(): Sound {
		return new Sound(SoundId.ROBOT_DIE);
	}

	getState(): FloatingRobotState {
		return this.state;
	}

	serialize(): Record<string, unknown> {
		return { ...super.serialize(), state: this.state };
	}

	static deserialize(data: any): FloatingRobot {
		const m = new FloatingRobot(deserializeXY(data.xy));
		m.state = data.state;
		m.restoreMobFields(data);
		return m;
	}
}
